import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { Flex } from 'reflexbox';
import { writeTeamName } from '../../helpers/dbFunctions';
import profanityChecker from '../helpers/profanityChecker';
import emojiOptions from '../../helpers/emojiOptions';
import CopyItem from '../common/CopyItem';

const TeamDetailsBottomSheet = ({ open, onClose, onEditProfile }) => {
  const { uid } = useSelector((state) => state.user);
  const { currentTeamId, teamDetails } = useSelector((state) => state.team);
  const { teammateInfo = {}, hostId, memberIds = [] } = teamDetails || {};
  const memberIdsArr = Array.isArray(memberIds) ? memberIds : Object.values(memberIds || {});
  // When hostId is missing (legacy teams), treat first member as host for display
  const effectiveHostId = (hostId != null && hostId !== '') ? hostId : memberIdsArr[0];

  const [teamName, setTeamNameLocal] = useState(teamDetails?.teamName || '');
  const [profanityError, setProfanityError] = useState(false);
  const [teamNameSaved, setTeamNameSaved] = useState(false);

  useEffect(() => {
    if (open) {
      setTeamNameLocal(teamDetails?.teamName || '');
      setProfanityError(false);
      setTeamNameSaved(false);
    }
  }, [open]);

  const handleTeamNameChange = (event) => {
    setTeamNameLocal(event.target.value);
    setProfanityError(!!profanityChecker(event.target.value));
    setTeamNameSaved(false);
  };

  const handleSaveTeamName = () => {
    if (!profanityError && currentTeamId) {
      writeTeamName(currentTeamId, teamName);
      setTeamNameSaved(true);
    }
  };

  const playerCount = teammateInfo ? Object.keys(teammateInfo).length : 0;

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '85vh',
        },
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 3, mb: 1 }}>
        {/* Team Details header with close button */}
        <Box sx={{ position: 'relative', mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            Team Details
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <TextField
            label="Team Name"
            placeholder="No name set"
            value={teamName}
            onChange={handleTeamNameChange}
            fullWidth
            size="small"
            error={profanityError}
          />
          <Button
            variant="contained"
            onClick={handleSaveTeamName}
            disabled={profanityError}
            sx={{ minWidth: 110, mt: 0.5 }}
            startIcon={teamNameSaved ? <CheckIcon /> : null}
          >
            {teamNameSaved ? 'Saved' : 'Save'}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
          Name will be used for the leaderboard
        </Typography>
        {profanityError && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
            This name does not meet our community guidelines. Please use a different name.
          </Typography>
        )}

        {/* Team Members */}
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 1.5 }}>
          Team Members
        </Typography>
        <Flex row sx={{ mb: 2 }}>
          <Typography variant="body1">{playerCount} players</Typography>
          <Flex style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body1" style={{ paddingLeft: '8px' }}>
              ·  Join code: <span style={{ fontWeight: 'bold', letterSpacing: '2px' }}>{currentTeamId}</span>
            </Typography>
            <CopyItem thingToCopy={currentTeamId} />
          </Flex>
        </Flex>

        {/* Member list */}
        {teammateInfo && Object.entries(teammateInfo).map(([memberUid, memberInfo]) => (
          <Flex
            key={memberUid}
            alignItems="center"
            sx={{ py: 2 }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                ...(memberInfo.color && { bgcolor: memberInfo.color }),
              }}
            >
              {memberInfo.character >= 0 && emojiOptions[memberInfo.character] ? (
                emojiOptions[memberInfo.character]
              ) : (
                (memberInfo.name || '?')[0]
              )}
            </Avatar>
            <Box sx={{ flex: 1, pl: 1.5 }}>
              <Typography variant="body1" fontWeight={500}>
                {memberInfo.name || 'Unnamed'}
              </Typography>
              {memberUid === uid && (
                <Typography variant="caption" color="text.secondary">
                  You
                  {effectiveHostId === uid && ' · Game host'}
                </Typography>
              )}
              {memberUid !== uid && effectiveHostId === memberUid && (
                <Typography variant="caption" color="text.secondary">
                  Game host
                </Typography>
              )}
            </Box>
            {memberUid === uid && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon fontSize="small" />}
                onClick={() => {
                  onClose();
                  onEditProfile?.();
                }}
              >
                Edit
              </Button>
            )}
          </Flex>
        ))}
      </Box>
    </Drawer>
  );
};

TeamDetailsBottomSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onEditProfile: PropTypes.func,
};

export default TeamDetailsBottomSheet;
